import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import {
  trackPricesSelected,
  trackProductsSelected,
} from "@/features/analytics/trackers";
import { trackPlanPageViewed } from "@/features/analytics/trackers/plan-page-viewed";
import { ProductGroupCard } from "@/features/onboarding/components/choose-products/product-group-card";
import { SelectedProduct } from "@/features/onboarding/components/choose-products/selected-product";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import { LogoLoader } from "@/shared/components";
import { CATEGORY_SLUG, PRODUCT_SLUG } from "@/shared/domains";
import { errorToast, SectionHeader } from "@/shared/ui";
import { QuestionNavigation, QuestionStep } from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";

export const Product = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();

  const user = useOnboardingUser();
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const setSelectedCategories = useOnboardingStore(
    (state) => state.setSelectedCategories,
  );
  const addSelectedProduct = useOnboardingStore(
    (state) => state.addSelectedProduct,
  );

  const {
    data: productGroups,
    isLoading,
    isError,
  } = useQuery(
    productsQueriesStore.products.category._ctx.products({
      categorySlug: CATEGORY_SLUG.WEIGHT_LOSS,
    }),
  );

  useEffect(() => {
    if (isError) {
      errorToast(
        t`We couldn't load the available treatments. Please try again.`,
      );
    }
  }, [isError]);

  useEffect(() => {
    if (!productGroups) {
      return;
    }

    const tirzepatideProduct = productGroups
      .flatMap((group) => group.products)
      .find((product) => product.slug === PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE);

    const category = tirzepatideProduct?.category;
    if (!category) {
      return;
    }

    // Seed the selected category so product/price selection can be stored.
    const existingCategories = useOnboardingStore.getState().selectedCategories;
    if (!existingCategories.some((current) => current.id === category.id)) {
      setSelectedCategories([{ ...category, isNew: false }]);
    }

    const alreadySelected = useOnboardingStore
      .getState()
      .selectedCategories.some(
        (current) => current.selectedProduct?.id === tirzepatideProduct.id,
      );

    if (!alreadySelected) {
      addSelectedProduct(tirzepatideProduct);
    }
  }, [productGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      return;
    }

    trackPlanPageViewed({
      customerIoAnalytics: analytics,
      user,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  if (!user) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.CONTACT_DETAILS} replace />;
  }

  const handleContinue = () => {
    const hasSelectedPrice = selectedCategories.some(
      (category) => category.selectedProduct?.selectedPrice,
    );

    if (!hasSelectedPrice) {
      errorToast(t`Please select a product and package to continue.`);
      return;
    }

    trackProductsSelected({
      selectedCategories,
      userId: user.email,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });
    trackPricesSelected({
      selectedCategories,
      userId: user.email,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });

    navigate(QUESTIONNAIRE_ROUTE.CHECKOUT);
  };

  return (
    <QuestionStep
      size="sm"
      footer={
        <QuestionNavigation
          nextType="button"
          nextLabel={t`Continue to checkout`}
          onNext={handleContinue}
        />
      }
    >
      <LogoLoader show={isLoading} fullscreen />

      <SectionHeader
        title={t`Choose your treatment`}
        description={t`Select the program that works best for you.`}
      />

      <div className="flex w-full flex-col gap-8">
        <section className="flex flex-col gap-4">
          {productGroups?.map((group) => (
            <ProductGroupCard key={group.id} group={group} />
          ))}
        </section>

        <SelectedProduct />
      </div>
    </QuestionStep>
  );
};
